import React from "react";
import { useNotifications } from "utils/hooks";
import { EARNINGS_CALCULATOR_CONSTANTS } from "./EarningsCalculator.constants";
import { useEarningsCalculator } from "./useEarningsCalculator";

export const EarningsCalculator = ({ referralOverview, link }) => {
  const {
    SUBTITLE,
    TITLE,
    PEOPLE_LABEL,
    PLAN_LABEL,
    YOU_COULD_EARN_LABEL,
    COPY_BUTTON_TEXT,
    PEOPLE_OPTIONS,
    PLANS,
    ANNUAL_SUFFIX,
  } = EARNINGS_CALCULATOR_CONSTANTS;

  const { pushNotification } = useNotifications();

  const {
    selectedPeopleCount,
    setSelectedPeopleCount,
    selectedPlanId,
    setSelectedPlanId,
    activeRate,
    formattedTotalEarnings,
    formattedPerReferral,
    formattedPlanPrice,
    breakdownRows,
  } = useEarningsCalculator(referralOverview);

  const handleCopy = () => {
    if (navigator && navigator.clipboard && link) {
      navigator.clipboard.writeText(link);
      pushNotification("Link Copied!", {
        variant: "outlined",
        color: "primary",
      });
    }
  };

  return (
    <section className="tw-w-full tw-my-12">
      {/* Header */}
      <div className="tw-text-center tw-mb-8">
        <span className="tw-text-xs tw-font-bold tw-tracking-wider tw-text-[#4E39DF] tw-uppercase tw-block tw-mb-2">
          {SUBTITLE}
        </span>
        <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-extrabold tw-text-slate-900 tw-m-0 tw-mb-3">
          {TITLE}
        </h2>
        <p className="tw-text-sm sm:tw-text-base tw-text-slate-500 tw-mx-auto tw-leading-relaxed tw-m-0">
          Pick how many people you refer and the plan they choose. You earn{" "}
          {activeRate}% of their annual plan.
        </p>
      </div>

      {/* Main Dual Card Widget */}
      <div className="tw-max-w-4xl tw-mx-auto tw-bg-[#FAFAFD] tw-border tw-border-solid tw-border-[#EEECF5] tw-rounded-3xl tw-overflow-hidden tw-shadow-sm tw-grid tw-grid-cols-1 md:tw-grid-cols-12">
        {/* Left Interactive Section */}
        <div className="md:tw-col-span-6 tw-p-6 sm:tw-p-8 tw-flex tw-flex-col tw-justify-between tw-gap-6">
          {/* People Count Selector */}
          <div>
            <span className="tw-text-sm tw-font-bold tw-text-slate-900 tw-block tw-mb-3">
              {PEOPLE_LABEL}
            </span>
            <div className="tw-grid tw-grid-cols-3 tw-gap-3">
              {PEOPLE_OPTIONS.map((option) => {
                const isSelected = selectedPeopleCount === option.VALUE;
                return (
                  <button
                    key={option.ID}
                    type="button"
                    onClick={() => setSelectedPeopleCount(option.VALUE)}
                    className={`tw-py-3 tw-px-4 tw-rounded-xl tw-font-bold tw-text-base tw-transition-all tw-cursor-pointer tw-border tw-border-solid ${
                      isSelected
                        ? "tw-bg-[#4E39DF] tw-text-white tw-border-[#4E39DF] tw-shadow-sm"
                        : "tw-bg-white tw-text-slate-900 tw-border-slate-200 hover:tw-border-[#4E39DF]"
                    }`}
                  >
                    {option.LABEL}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Plan Selector */}
          <div>
            <span className="tw-text-sm tw-font-bold tw-text-slate-900 tw-block tw-mb-3">
              {PLAN_LABEL}
            </span>
            <div className="tw-space-y-3">
              {PLANS.map((plan) => {
                const isSelected = selectedPlanId === plan.ID;
                return (
                  <button
                    key={plan.ID}
                    type="button"
                    onClick={() => setSelectedPlanId(plan.ID)}
                    className={`tw-w-full tw-p-4 tw-rounded-xl tw-flex tw-items-center tw-justify-between tw-transition-all tw-cursor-pointer tw-border tw-border-solid ${
                      isSelected
                        ? "tw-bg-[#F1EFFD] tw-border-[#4E39DF] tw-text-slate-900"
                        : "tw-bg-white tw-border-slate-200 tw-text-slate-900 hover:tw-border-slate-300"
                    }`}
                  >
                    <span className="tw-font-bold tw-text-sm sm:tw-text-base">
                      {plan.NAME}
                    </span>
                    <span className="tw-text-xs sm:tw-text-sm tw-text-slate-500 tw-font-medium">
                      {plan.DISPLAY_PRICE}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Purple Earnings Summary Panel */}
        <div
          style={{
            background:
              "linear-gradient(135deg, rgb(108, 92, 231) 0%, rgb(79, 63, 209) 100%)",
            padding: "32px 28px",
          }}
          className="md:tw-col-span-6 tw-text-white tw-flex tw-flex-col tw-justify-between tw-gap-6"
        >
          <div>
            <span className="tw-text-xs sm:tw-text-sm tw-font-medium tw-text-white/80 tw-block tw-mb-1">
              {YOU_COULD_EARN_LABEL}
            </span>
            <div className="tw-text-3xl sm:tw-text-5xl tw-font-extrabold tw-tracking-tight tw-mb-2">
              {formattedTotalEarnings}
            </div>
            <div className="tw-text-xs sm:tw-text-sm tw-text-white/80 tw-font-normal">
              {selectedPeopleCount} × {formattedPerReferral} ({activeRate}% of{" "}
              {formattedPlanPrice}
              {ANNUAL_SUFFIX})
            </div>
          </div>

          {/* Breakdown Table */}
          <div className="tw-border-t tw-border-l-0 tw-border-r-0 tw-border-b-0 tw-border-solid tw-border-white/20 tw-pt-4 tw-space-y-2">
            {breakdownRows.map((row) => (
              <div
                key={row.id}
                className="tw-flex tw-items-center tw-justify-between tw-text-xs sm:tw-text-sm tw-text-white/90"
              >
                <span>{row.label}</span>
                <span className="tw-font-bold">{row.formattedAmount}</span>
              </div>
            ))}
          </div>

          {/* CTA: Copy my referral link */}
          <div>
            <button
              type="button"
              onClick={handleCopy}
              className="tw-w-full sm:tw-w-auto tw-bg-white tw-text-[#4E39DF] hover:tw-bg-gray-50 tw-font-bold tw-text-sm tw-px-6 tw-py-3 tw-rounded-xl tw-border-0 tw-cursor-pointer tw-transition-colors tw-shadow-sm"
            >
              {COPY_BUTTON_TEXT}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
